import { useEffect } from "react";
import { useI18n } from "../../../../packages/excalidraw/i18n";
import "./FeedbackNudge.scss";

interface Props {
  visible: boolean;
  onAccept: () => void;
  onDismiss: () => void;
}

/**
 * The feedback coach-mark: a bubble tethered to the feedback button in the
 * bottom-right footer, with a tail pointing at it.
 *
 * Anchored rather than centred, so dismissing it still teaches where feedback
 * lives. Never auto-hides, and never takes focus — the canvas keeps the
 * keyboard.
 */
const FeedbackNudge: React.FC<Props> = ({ visible, onAccept, onDismiss }) => {
  const { t } = useI18n();
  // Escape defers. Bound on `document` because the node is not focused, and
  // removed when hidden so it never swallows the editor's own Escape.
  useEffect(() => {
    if (!visible) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onDismiss();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [visible, onDismiss]);

  if (!visible) {
    return null;
  }

  return (
    <div
      className="feedback-nudge"
      data-testid="feedback-nudge"
      // `status`, not `dialog`: a dialog promises focus this never takes. A
      // polite live region announces the offer without moving the keyboard.
      role="status"
      aria-live="polite"
    >
      <p className="feedback-nudge__title">{t("mindmap.feedbackNudgeTitle")}</p>
      <p className="feedback-nudge__body">{t("mindmap.feedbackNudgeBody")}</p>

      <div className="feedback-nudge__actions">
        <button
          type="button"
          className="feedback-nudge__primary"
          onClick={onAccept}
        >
          {t("mindmap.feedbackNudgeAccept")}
        </button>
        <button
          type="button"
          className="feedback-nudge__secondary"
          onClick={onDismiss}
        >
          {t("mindmap.feedbackNudgeDismiss")}
        </button>
      </div>

      <span className="feedback-nudge__tail" aria-hidden="true" />
    </div>
  );
};

export default FeedbackNudge;
